import { z } from 'zod'
import {
  ALERTA_SEVERIDAD,
  ALERTA_TIPO,
  CREDENCIAL_TIPO,
  JUGADOR_ESTADO,
  JUGADOR_ESTADO_CON_CHECKIN,
  JUGADOR_ESTADO_SIN_CHECKIN,
  MESA_ESTADO,
  MESA_TIPO,
  PARTICIPANTE_ESTADO,
  RESULTADO_MOTIVO,
  RESULTADO_TIPO,
  RONDA_ESTADO,
  SANCION_CON_VENCIMIENTO,
  SANCION_TIPO,
  STAFF_ROL,
  SYNC_OPERACION,
  TORNEO_ESTADO,
  TORNEO_MODALIDAD,
  TORNEO_TIPO,
  USUARIO_ESTADO,
  USUARIO_ROL,
} from './enums.js'

const uuid = z.string().uuid()
/** Timestamps siempre en UTC ISO-8601: es el formato que entiende SQLite y Postgres por igual. */
const fecha = z.string().datetime({ offset: true })
const texto = z.string().trim().min(1)
const entero = z.number().int()
const positivo = z.number().int().positive()
const noNegativo = z.number().int().nonnegative()

// ---------------------------------------------------------------------------
// Identidad
// ---------------------------------------------------------------------------

export const usuarioSchema = z
  .object({
    id: uuid,
    email: z.string().email().nullable(),
    passwordHash: z.string().nullable(),
    nombre: texto,
    apodo: z.string().trim().nullable(),
    /** El número global del jugador. Nulo solo en fichas anteriores a 0008. */
    numero: positivo.nullable(),
    rol: z.enum(USUARIO_ROL),
    estado: z.enum(USUARIO_ESTADO),
    creadoPor: uuid.nullable(),
    ultimoIngresoAt: fecha.nullable(),
    createdAt: fecha,
    updatedAt: fecha,
  })
  // Los mismos invariantes que los CHECK de la base, para atajarlos antes de escribir.
  .refine((u) => (u.email === null) === (u.estado === 'sin_acceso'), {
    message: 'Sin email la cuenta debe estar en sin_acceso, y viceversa',
    path: ['estado'],
  })
  .refine((u) => u.estado !== 'invitado' || u.passwordHash === null, {
    message: 'Una cuenta invitada todavía no definió su contraseña',
    path: ['passwordHash'],
  })
  .refine(
    (u) => !['activo', 'suspendido'].includes(u.estado) || u.passwordHash !== null,
    { message: 'Una cuenta activa o suspendida tiene que tener contraseña', path: ['passwordHash'] },
  )
  .refine((u) => u.rol === 'user' || u.estado !== 'sin_acceso', {
    message: 'Nadie puede administrar sin poder ingresar',
    path: ['rol'],
  })

export type Usuario = z.infer<typeof usuarioSchema>

export const credencialTokenSchema = z.object({
  id: uuid,
  usuarioId: uuid,
  tokenHash: z.string(),
  tipo: z.enum(CREDENCIAL_TIPO),
  expiraAt: fecha,
  usadoAt: fecha.nullable(),
  createdAt: fecha,
})
export type CredencialToken = z.infer<typeof credencialTokenSchema>

// ---------------------------------------------------------------------------
// Torneo
// ---------------------------------------------------------------------------

/**
 * La liga: una agrupación de torneos, sin reglamento ni puntaje propio.
 *
 * Sobrevive a sus torneos, al revés que el equipo, que nace y muere con el
 * evento: por eso cuelga de quien organiza y no de un torneo. Lo que habilita
 * son las dos cosas del día que se crea una fecha nueva: la lista agrupada y la
 * nómina del torneo anterior de la misma liga y modalidad. Ver la migración
 * 0023.
 */
export const ligaSchema = z.object({
  id: uuid,
  organizadorId: uuid,
  nombre: texto,
  /**
   * Quién la lleva, como se lo nombra en el salón. Nulo solo en las ligas
   * anteriores a la 0026: el alta lo pide. Con `default` para que una app más
   * vieja, que no lo manda, siga pudiendo publicar.
   */
  responsable: texto.nullable().default(null),
  /** Lo que se quiera dejar anotado de la liga. Opcional. */
  comentarios: z.string().nullable().default(null),
  createdAt: fecha,
})
export type Liga = z.infer<typeof ligaSchema>

export const torneoSchema = z
  .object({
    id: uuid,
    organizadorId: uuid,
    /**
     * De qué liga es esta fecha, o `null` si el torneo es suelto.
     *
     * El nulo es un caso normal y no un dato a medio cargar: el amistoso del
     * sábado no es de ninguna liga y nunca lo va a ser.
     *
     * Con `default` y no solo `nullable`, que es lo único de este esquema que lo
     * lleva. El motivo es la cola: esta es la fila del torneo, o sea la primera
     * entrada de CUALQUIER lote, y una app anterior a la migración 0023 no manda
     * la columna. Sin el default, el día que se despliega el servidor nuevo esa
     * app deja de publicar entera, por un campo que en su base no existe.
     */
    ligaId: uuid.nullable().default(null),
    nombre: texto,
    descripcion: z.string().nullable(),
    tipo: z.enum(TORNEO_TIPO),
    modalidad: z.enum(TORNEO_MODALIDAD),
    objetivoPuntos: positivo,
    numeroRondas: positivo,
    // Mínimo 1: el límite dice cuántas veces pueden tocar juntos, y 0 sería
    // pedir que no se crucen nunca, algo que en Individual no se puede cumplir.
    limiteRepeticionCompanero: positivo.nullable(),
    /**
     * El mismo número que `limiteRepeticionCompanero`, para Equipos.
     *
     * Son dos columnas y no una porque el CHECK `torneo_limite_solo_individual`
     * de la migración 0001 ata la primera a Individual, y en SQLite un CHECK de
     * tabla no se relaja sin rehacer `torneo`. Ver la migración 0012. Del lado
     * del emparejador vuelven a ser una sola cosa.
     *
     * En Equipos es opcional: en nulo la regla no aplica y la rotación de
     * parejas es la fija del reglamento.
     */
    limiteRepeticionCompaneroEquipos: positivo.nullable(),
    puntosPorMesaIncompleta: noNegativo,
    tamanoEquipo: positivo.nullable(),
    semillaAleatoria: z.string(),
    estado: z.enum(TORNEO_ESTADO),
    syncHabilitado: z.boolean(),
    fechaInicio: fecha.nullable(),
    fechaFin: fecha.nullable(),
    createdAt: fecha,
    updatedAt: fecha,
  })
  .refine((t) => (t.modalidad === 'equipos') === (t.tamanoEquipo !== null), {
    message: 'tamanoEquipo va si y solo si la modalidad es equipos',
    path: ['tamanoEquipo'],
  })
  .refine((t) => (t.modalidad === 'individual') === (t.limiteRepeticionCompanero !== null), {
    message: 'El límite de repetición de compañero solo aplica a Individual',
    path: ['limiteRepeticionCompanero'],
  })
  .refine((t) => t.limiteRepeticionCompaneroEquipos === null || t.modalidad === 'equipos', {
    message: 'El límite de repetición de compañero por equipos solo aplica a Equipos',
    path: ['limiteRepeticionCompaneroEquipos'],
  })

export type Torneo = z.infer<typeof torneoSchema>

export const torneoStaffSchema = z.object({
  torneoId: uuid,
  usuarioId: uuid,
  rol: z.enum(STAFF_ROL),
  asignadoPor: uuid.nullable(),
  revocadoAt: fecha.nullable(),
  createdAt: fecha,
})
export type TorneoStaff = z.infer<typeof torneoStaffSchema>

export const equipoSchema = z.object({
  id: uuid,
  torneoId: uuid,
  nombre: texto,
  /**
   * De dónde viene el equipo. Código ISO 3166-1 alfa-2; ver [`PAISES`].
   *
   * Obligatorio en el alta y `nullable` igual, por lo mismo que `usuario.pais`:
   * el null es el de los equipos cargados antes de la migración 0021, no un
   * valor que se pueda guardar hoy. Quien no lo deja pasar es cada comando que
   * escribe, y del lado de la base el CHECK de la columna.
   */
  pais: z.string().nullable(),
  createdAt: fecha,
})
export type Equipo = z.infer<typeof equipoSchema>

/**
 * La pareja de la modalidad Parejas: nómina, no unidad de ranking.
 *
 * Nace en la inscripción, con su número, y es lo que el organizador chequea el
 * día del torneo. El `participante` que puntúa sale de ella recién al INICIAR,
 * uno por pareja confirmada. Ver la migración 0018.
 *
 * No tiene nombre a propósito: se la conoce por su número, que es del torneo y
 * no del club —`usuario.numero` es otra cosa— y es lo único que sale en la
 * proyección.
 */
export const parejaSchema = z.object({
  id: uuid,
  torneoId: uuid,
  numero: positivo,
  /**
   * Cuándo se dio de baja, o nulo si sigue en la nómina.
   *
   * La fila no se borra: es lo que mantiene su número fuera de circulación. Una
   * pareja dada de baja no libera el número porque ya se imprimieron listas con
   * él.
   */
  bajaAt: fecha.nullable(),
  createdAt: fecha,
})
export type Pareja = z.infer<typeof parejaSchema>

// ---------------------------------------------------------------------------
// Participación
// ---------------------------------------------------------------------------

export const torneoJugadorSchema = z
  .object({
    id: uuid,
    torneoId: uuid,
    usuarioId: uuid,
    equipoId: uuid.nullable(),
    /**
     * Con quién juega, en la modalidad Parejas. Nulo en las otras dos.
     *
     * El gemelo exacto de `equipoId`, y con la misma baja suave: si la pareja
     * se deshace los dos quedan sueltos en la nómina en vez de salir del
     * torneo, porque el trigger de altas tardías no los dejaría volver a entrar
     * por un cambio de compañero. Ver la migración 0018.
     */
    parejaId: uuid.nullable(),
    estado: z.enum(JUGADOR_ESTADO),
    /**
     * Si hoy no se sienta a jugar. Ver la migración 0010.
     *
     * NO es el estado `ausente`, aunque se llamen igual. Ese quiere decir que
     * el torneo arrancó sin que la persona llegara, y por eso nunca fue
     * participante: no está en el ranking ni en ninguna mesa. Esta bandera es
     * de quien SÍ entró al torneo y compite —confirmado, con sus rondas
     * jugadas— pero que en esta ronda no está: el emparejamiento lo manda al
     * fondo y cobra los puntos por mesa incompleta sin ocuparle la silla a
     * nadie. Los dos nunca conviven: al que quedó en estado `ausente` no se lo
     * empareja, así que su bandera no cambia nada.
     */
    ausente: z.boolean(),
    /**
     * Si esta inscripción nació de una sustitución. Ver la migración 0015.
     *
     * Es lo único que le abre la puerta al trigger de altas tardías, que es
     * BEFORE INSERT y no puede saber que la fila que entra es un reemplazo.
     *
     * NO quiere decir «entró como suplente»: el que ya estaba inscripto y quedó
     * en estado `ausente` puede tomar una silla sin que se inserte nada, y
     * queda en `false`. Quién reemplazó a quién lo dice
     * `participante_sustitucion`, que es el hecho; esto es de la inscripción.
     */
    suplente: z.boolean(),
    checkinAt: fecha.nullable(),
    checkinPor: uuid.nullable(),
    createdAt: fecha,
  })
  // Los mismos casos que el CHECK jugador_checkin_coherente de la base.
  // `suspendido` queda libre a propósito: se puede sancionar a alguien antes
  // de que llegue al salón o después de haberlo confirmado.
  .refine((j) => !JUGADOR_ESTADO_CON_CHECKIN.includes(j.estado) || j.checkinAt !== null, {
    message: 'Un jugador confirmado o retirado tiene fecha de confirmación',
    path: ['checkinAt'],
  })
  .refine((j) => !JUGADOR_ESTADO_SIN_CHECKIN.includes(j.estado) || j.checkinAt === null, {
    message: 'Un jugador pendiente o ausente no tiene fecha de confirmación',
    path: ['checkinAt'],
  })

export type TorneoJugador = z.infer<typeof torneoJugadorSchema>

/**
 * La sanción es un hecho registrado, no un estado.
 *
 * `torneo_jugador.estado` dice dónde está el jugador hoy; esto dice qué pasó,
 * cuándo y quién lo decidió. Separarlas es lo que da historial: una verbal en
 * la ronda 2 y una roja en la 5 son dos filas, y el estado final no borra la
 * primera.
 */
export const torneoSancionSchema = z
  .object({
    id: uuid,
    torneoId: uuid,
    torneoJugadorId: uuid,
    /** Nulo si se sancionó antes de que arrancara el torneo. */
    rondaId: uuid.nullable(),
    tipo: z.enum(SANCION_TIPO),
    motivo: texto,
    /** Solo la negra, y siempre: es lo único que la distingue de la roja. */
    venceAt: fecha.nullable(),
    aplicadaPor: uuid,
    createdAt: fecha,
  })
  // El mismo predicado doble del CHECK de la columna en la base.
  .refine((s) => SANCION_CON_VENCIMIENTO.includes(s.tipo) === (s.venceAt !== null), {
    message: 'Solo la tarjeta negra lleva fecha de vencimiento, y la lleva siempre',
    path: ['venceAt'],
  })
export type TorneoSancion = z.infer<typeof torneoSancionSchema>

export const participanteSchema = z.object({
  id: uuid,
  torneoId: uuid,
  nombreDisplay: z.string().nullable(),
  /**
   * El número de la pareja, copiado al iniciar el torneo. Nulo fuera de Parejas.
   *
   * Se copia y no se lee por join por lo mismo que `desempate` se precalcula: el
   * participante es lo que sobrevive al resto del torneo, y las consultas que lo
   * leen son las de la tabla, la mesa, la hoja impresa y el tablero. Donde va en
   * nulo, esas consultas caen al número de la persona.
   */
  numero: positivo.nullable(),
  /** Valor de sorteo precalculado. No se recalcula nunca: define el orden en los empates. */
  desempate: z.string(),
  estado: z.enum(PARTICIPANTE_ESTADO),
  createdAt: fecha,
})
export type Participante = z.infer<typeof participanteSchema>

export const participanteMiembroSchema = z.object({
  participanteId: uuid,
  torneoJugadorId: uuid,
  torneoId: uuid,
  orden: z.union([z.literal(1), z.literal(2)]),
})
export type ParticipanteMiembro = z.infer<typeof participanteMiembroSchema>

/**
 * La sustitución es un hecho registrado, igual que la sanción.
 *
 * `participante_miembro` dice quién ocupa la silla hoy; esto dice qué pasó, en
 * qué ronda y quién lo decidió. Dos sustituciones sobre la misma silla son dos
 * filas, y la segunda no borra la primera.
 *
 * Con la ronda anotada, cada `mesa_asignacion` queda atribuible a una persona:
 * es lo que permite separar más adelante quién jugó qué, sin lo cual el dato se
 * pierde en el momento de la sustitución y no se recupera.
 */
export const participanteSustitucionSchema = z
  .object({
    id: uuid,
    torneoId: uuid,
    /** La silla, que no cambia: conserva su id, su desempate y su acumulado. */
    participanteId: uuid,
    saleTorneoJugadorId: uuid,
    entraTorneoJugadorId: uuid,
    /**
     * En qué ronda se hizo, si había alguna en juego. Es historia.
     *
     * No es lo mismo que `desdeRonda`, aunque casi siempre coincidan: ver la
     * migración 0016.
     */
    rondaId: uuid.nullable(),
    /**
     * Desde qué ronda se le acredita al que entró. Es contabilidad.
     *
     * Si la mesa de esa silla ya estaba cargada cuando el titular se fue, esa
     * ronda la jugó él y el suplente empieza a contar en la siguiente. Va como
     * número y no como referencia a la ronda porque esa ronda puede no existir
     * todavía.
     */
    desdeRonda: positivo,
    motivo: texto,
    registradaPor: uuid,
    createdAt: fecha,
  })
  .refine((s) => s.saleTorneoJugadorId !== s.entraTorneoJugadorId, {
    message: 'Nadie se reemplaza a sí mismo',
    path: ['entraTorneoJugadorId'],
  })
export type ParticipanteSustitucion = z.infer<typeof participanteSustitucionSchema>

// ---------------------------------------------------------------------------
// Desarrollo — específico de Suizo
// ---------------------------------------------------------------------------

export const rondaSchema = z.object({
  id: uuid,
  torneoId: uuid,
  numero: positivo,
  estado: z.enum(RONDA_ESTADO),
  emparejadaAt: fecha.nullable(),
  cerradaAt: fecha.nullable(),
  createdAt: fecha,
})
export type Ronda = z.infer<typeof rondaSchema>

export const mesaSchema = z.object({
  id: uuid,
  rondaId: uuid,
  numero: positivo,
  tipo: z.enum(MESA_TIPO),
  estado: z.enum(MESA_ESTADO),
})
export type Mesa = z.infer<typeof mesaSchema>

export const mesaAsignacionSchema = z.object({
  id: uuid,
  mesaId: uuid,
  rondaId: uuid,
  torneoId: uuid,
  participanteId: uuid,
  /** Nulo en las mesas incompletas: sin rival no hay bandos. */
  bando: z.union([z.literal(1), z.literal(2)]).nullable(),
  orden: positivo,
})
export type MesaAsignacion = z.infer<typeof mesaAsignacionSchema>

export const resultadoSchema = z
  .object({
    mesaAsignacionId: uuid,
    /** Copia del objetivo del torneo al momento de cargar: blinda los resultados viejos. */
    objetivoPuntos: positivo,
    puntosHechos: noNegativo,
    puntosRecibidos: noNegativo,
    resultado: z.enum(RESULTADO_TIPO),
    motivo: z.enum(RESULTADO_MOTIVO),
    registradoPor: uuid.nullable(),
    createdAt: fecha,
    updatedAt: fecha,
  })
  .refine((r) => !['ausencia', 'abandono'].includes(r.motivo) || r.resultado === 'perdida', {
    message: 'El que abandona o no se presenta, pierde',
    path: ['resultado'],
  })
  .refine(
    (r) =>
      !['bye', 'mesa_incompleta'].includes(r.motivo) ||
      (r.resultado === 'ganada' && r.puntosRecibidos === 0),
    { message: 'Sin rival: ganada y con recibidos en 0', path: ['puntosRecibidos'] },
  )

export type Resultado = z.infer<typeof resultadoSchema>

export const resultadoHistorialSchema = z.object({
  id: uuid,
  mesaAsignacionId: uuid,
  puntosHechosPrevio: noNegativo,
  puntosRecibidosPrevio: noNegativo,
  resultadoPrevio: z.enum(RESULTADO_TIPO),
  motivoPrevio: z.enum(RESULTADO_MOTIVO),
  modificadoPor: uuid.nullable(),
  motivoCorreccion: z.string().nullable(),
  createdAt: fecha,
})
export type ResultadoHistorial = z.infer<typeof resultadoHistorialSchema>

export const rankingSnapshotSchema = z.object({
  rondaId: uuid,
  participanteId: uuid,
  torneoId: uuid,
  posicion: positivo,
  ganadas: noNegativo,
  perdidas: noNegativo,
  efectividad: entero,
  puntosFavor: noNegativo,
  resueltoPorSorteo: z.boolean(),
  createdAt: fecha,
})
export type RankingSnapshot = z.infer<typeof rankingSnapshotSchema>

// ---------------------------------------------------------------------------
// Trazabilidad y sincronización
// ---------------------------------------------------------------------------

export const torneoAlertaSchema = z.object({
  id: uuid,
  torneoId: uuid,
  rondaId: uuid.nullable(),
  mesaId: uuid.nullable(),
  tipo: z.enum(ALERTA_TIPO),
  severidad: z.enum(ALERTA_SEVERIDAD),
  mensaje: texto,
  detalle: z.unknown().nullable(),
  resuelta: z.boolean(),
  createdAt: fecha,
})
export type TorneoAlerta = z.infer<typeof torneoAlertaSchema>

export const syncOutboxSchema = z.object({
  seq: z.number().int().positive(),
  torneoId: uuid,
  entidad: texto,
  entidadId: uuid,
  operacion: z.enum(SYNC_OPERACION),
  payload: z.string(),
  createdAt: fecha,
  enviadoAt: fecha.nullable(),
  intentos: noNegativo,
  ultimoError: z.string().nullable(),
})
export type SyncOutbox = z.infer<typeof syncOutboxSchema>
